<template>
  <div id="app" class="log-edit-page" v-wechat-title="titleTop">
    
    <!-- Header -->
    <div class="page-header">
      <van-icon name="cross" class="nav-icon" @click="goBack" />
      <div class="nav-title">
        {{ isNew ? '新建日志' : '编辑日志' }}
        <span class="nav-subtitle">{{ isNew ? 'NEW ENTRY' : 'EDIT MODE' }}</span>
      </div>
      <div class="nav-btn" @click="onSave" :class="{ 'disabled': buttonLoading }">
        {{ buttonLoading ? '...' : '保存' }}
      </div>
    </div>

    <div class="form-container">
        
        <!-- 1. Meta Data -->
        <div class="form-section-title">
            <span class="fst-cn">基础信息</span><span class="fst-en">MISSION DATA</span>
        </div>
        
        <div class="input-grid-2">
            <!-- 日期 -->
            <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">日期</span><span class="lbl-en">DATE</span></div>
                <div class="input-wrapper" @click="showDatePicker = true">
                    <input type="text" readonly class="hud-input pointer" :value="formatDate(dataForm.dateOfStar, 'yyyy/MM/dd')" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
            <!-- 时间 -->
            <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">入水时间</span><span class="lbl-en">ENTRY TIME</span></div>
                <div class="input-wrapper" @click="onSelectTime">
                    <input type="text" readonly class="hud-input pointer" :value="formatDate(dataForm.dateOfStar, 'hh:mm')" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
        </div>

        <!-- 2. Location -->
        <div class="form-section-title">
            <span class="fst-cn">位置坐标</span><span class="fst-en">COORDINATES</span>
        </div>
        <div class="input-grid-2">
            <!-- 国家 -->
            <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">国家</span><span class="lbl-en">COUNTRY</span></div>
                <div class="input-wrapper" @click="onCountryPicker">
                    <input type="text" readonly class="hud-input pointer" :value="getCountryName(dataForm.country) || '选择国家'" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
            <!-- 地区 -->
            <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">地区</span><span class="lbl-en">REGION</span></div>
                <input type="text" class="hud-input" v-model="dataForm.areaName" placeholder="例如：巴厘岛">
            </div>
        </div>
        <!-- 潜点 -->
        <div class="hud-input-group">
            <div class="label-row"><span class="lbl-cn">潜点名称</span><span class="lbl-en">DIVE SITE</span></div>
            <input type="text" class="hud-input" v-model="dataForm.divePlaceName" placeholder="输入潜点名称..." style="font-size:18px;">
        </div>
        <!-- 潜店 -->
        <div class="hud-input-group">
            <div class="label-row"><span class="lbl-cn">潜店</span><span class="lbl-en">DIVE SHOP</span></div>
            <input type="text" class="hud-input" v-model="dataForm.shopName" placeholder="可选">
        </div>

        <!-- 3. Stats (Pickers) -->
        <div class="form-section-title">
            <span class="fst-cn">核心数据</span><span class="fst-en">TELEMETRY</span>
        </div>
        <div class="input-grid-2">
            <!-- 最大深度 -->
            <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">最大深度 (m)</span><span class="lbl-en">DEPTH</span></div>
                <div class="input-wrapper" @click="showDepthPicker = true">
                    <input type="text" readonly class="hud-input is-num pointer" :value="dataForm.maxDeep || '--'" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
            <!-- 潜水时长 -->
            <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">潜水时长</span><span class="lbl-en">TIME</span></div>
                <div class="input-wrapper" @click="showDurationPicker = true">
                    <input type="text" readonly class="hud-input is-num pointer" :value="formatDurationDisplay(dataForm.dateOfLen)" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
        </div>
        <div class="input-grid-3">
             <!-- 水温 -->
             <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">水温 (℃)</span><span class="lbl-en">TEMP</span></div>
                <div class="input-wrapper" @click="showTempPicker = true">
                    <input type="text" readonly class="hud-input is-num pointer" :value="dataForm.temparature || '--'" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
             <!-- 能见度 -->
             <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">能见度 (m)</span><span class="lbl-en">VIS</span></div>
                <div class="input-wrapper" @click="showVisPicker = true">
                    <input type="text" readonly class="hud-input is-num pointer" :value="dataForm.visibility || '--'" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
            <!-- 平均深度 -->
            <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">平均深度 (m)</span><span class="lbl-en">AVG</span></div>
                <div class="input-wrapper" @click="showAvgDepthPicker = true">
                    <input type="text" readonly class="hud-input is-num pointer" :value="dataForm.avgDeep || '--'" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
        </div>

        <!-- 4. Environment -->
        <div class="form-section-title">
             <span class="fst-cn">环境与设置</span><span class="fst-en">ENV & SETTINGS</span>
        </div>
        <div class="input-grid-2">
             <!-- 潜水类型 -->
             <div class="hud-input-group">
                <div class="label-row"><span class="lbl-cn">潜水类型</span><span class="lbl-en">TYPE</span></div>
                 <div class="input-wrapper" @click="showDiveTypePicker = true">
                    <input type="text" readonly class="hud-input pointer" :value="GetEnumText('Em_DiveType', dataForm.diveType) || 'SCUBA'" />
                    <van-icon name="arrow" class="input-arrow" />
                </div>
            </div>
            <!-- 私密开关 -->
            <div class="hud-input-group">
                 <div class="label-row"><span class="lbl-cn">私密日志</span><span class="lbl-en">PRIVATE</span></div>
                 <div class="switch-wrapper">
                    <van-switch v-model="dataForm.isPrivate" size="24px" active-color="#00E5FF" inactive-color="rgba(255,255,255,0.2)" />
                 </div>
            </div>
        </div>
        
        <!-- 天气选择 (直接操作响应式数据) -->
        <div class="hud-input-group">
            <div class="label-row"><span class="lbl-cn">天气状况</span><span class="lbl-en">WEATHER</span></div>
            <div class="weather-selector">
                <div
                    v-for="item in weatherList"
                    :key="item"
                    class="weather-icon-box"
                    :class="{ 'active': dataForm.weather === item }"
                    @click="setWeather(item)"
                >
                     <i :class="'iconfont icon' + item" />
                </div>
            </div>
        </div>
        
        <!-- 5. Visuals & Notes -->
        <div class="form-section-title">
            <span class="fst-cn">影像与笔记</span><span class="fst-en">VISUALS & LOG</span>
        </div>

        <!-- Hidden File Input -->
         <van-uploader
          v-model="fileList"
          multiple
          :max-count="9"
          class="hidden-uploader"
          ref="uploader"
        />

        <!-- Zero State -->
        <div v-if="fileList.length === 0" class="upload-zero-state" @click="triggerUpload">
            <van-icon name="photograph" size="24" style="margin-bottom:8px; opacity:0.8" />
            <div style="font-size:12px; color:rgba(255,255,255,0.6)">点击上传照片</div>
            <div style="font-size:9px; color:var(--accent-cyan); font-family:'Oswald'; margin-top:2px;">UPLOAD EVIDENCE</div>
        </div>

        <!-- Populated State -->
        <div v-else class="photo-list-scroll">
            <div class="photo-item" v-for="(item, index) in fileList" :key="index">
                <img :src="item.content || item.url" class="photo-thumb">
                <div class="delete-badge" @click="removeImg(index)">
                    <van-icon name="cross" />
                </div>
            </div>
            <div class="photo-add-small" @click="triggerUpload">
                <van-icon name="plus" size="20" />
                <span style="font-size:9px; font-weight:700; margin-top:2px;">ADD</span>
            </div>
        </div>

        <div class="hud-input-group">
            <div class="label-row"><span class="lbl-cn">潜水笔记</span><span class="lbl-en">OBSERVATIONS</span></div>
            <textarea class="hud-textarea" v-model="dataForm.about" placeholder="记录今天的潜水见闻..." maxlength="2048"></textarea>
        </div>

    </div>

    <!-- Footer Actions -->
    <div class="fixed-footer">
        <div class="footer-btn-group">
             <button v-if="!isNew" class="submit-btn btn-delete" @click="onDelete" :disabled="buttonLoading">
                <span>DELETE</span>
            </button>
            <button class="submit-btn btn-save" @click="onSave" :disabled="buttonLoading">
                确认记录 <span>CONFIRM ENTRY</span>
            </button>
        </div>
    </div>

    <!-- === POPUPS & PICKERS === -->

    <!-- Date Picker -->
    <van-popup v-model="showDatePicker" position="bottom">
      <van-datetime-picker
        v-model="tempDate"
        type="date"
        :min-date="minDate"
        :max-date="maxDate"
        title="DIVE DATE"
        @cancel="showDatePicker=false"
        @confirm="onConfrimDate"
      />
    </van-popup>

    <!-- Time Picker -->
    <van-popup v-model="showTimePicker" position="bottom">
      <van-datetime-picker
        v-model="tempTime"
        type="time"
        title="ENTRY TIME"
        @cancel="showTimePicker=false"
        @confirm="onConfrimTime"
      />
    </van-popup>

    <!-- Country Picker -->
    <van-popup v-model="showCountryPicker" position="bottom">
      <van-picker
        show-toolbar
        title="COUNTRY"
        :columns="countyList"
        :loading="loadingCounty"
        @cancel="showCountryPicker=false"
        @confirm="onConfrimCountry"
      />
    </van-popup>

    <!-- Dive Type Picker -->
    <van-popup v-model="showDiveTypePicker" position="bottom">
      <van-picker
        show-toolbar
        title="DIVE TYPE"
        value-key="text"
        :columns="diveTypeList"
        @cancel="showDiveTypePicker=false"
        @confirm="onConfrimDiveType"
      />
    </van-popup>

    <!-- Max Depth Picker (1-50m) -->
    <van-popup v-model="showDepthPicker" position="bottom">
      <van-picker
        show-toolbar
        title="MAX DEPTH"
        :columns="columnsDepth"
        @cancel="showDepthPicker=false"
        @confirm="onConfirmMaxDepth"
      />
    </van-popup>

    <!-- Duration Picker (Hour/Min) -->
    <van-popup v-model="showDurationPicker" position="bottom">
      <van-picker
        show-toolbar
        title="DURATION"
        :columns="columnsDuration"
        @cancel="showDurationPicker=false"
        @confirm="onConfirmDuration"
      />
    </van-popup>

    <!-- Temp Picker (0-40C) -->
    <van-popup v-model="showTempPicker" position="bottom">
      <van-picker
        show-toolbar
        title="TEMPERATURE"
        :columns="columnsTemp"
        @cancel="showTempPicker=false"
        @confirm="onConfirmTemp"
      />
    </van-popup>

    <!-- Vis Picker (0-50m, step 5) -->
    <van-popup v-model="showVisPicker" position="bottom">
      <van-picker
        show-toolbar
        title="VISIBILITY"
        :columns="columnsVis"
        @cancel="showVisPicker=false"
        @confirm="onConfirmVis"
      />
    </van-popup>
    
    <!-- Avg Depth Picker (Manual Override) -->
    <van-popup v-model="showAvgDepthPicker" position="bottom">
      <van-picker
        show-toolbar
        title="AVG DEPTH"
        :columns="columnsDepth"
        @cancel="showAvgDepthPicker=false"
        @confirm="onConfirmAvgDepth"
      />
    </van-popup>

  </div>
</template>

<script>
import request from '@/utils/request'
import { Button, Field, Uploader, DatetimePicker, Popup, Picker, Dialog, Switch, Icon, Toast } from 'vant'
import { GetEnumList, MyNotify, GetEnumText, formatDate, compressorImageList, GetDiveTitle } from '@/utils/base'

export default {
  name: 'DiveLogEdit',
  components: {
    [Button.name]: Button,
    [Field.name]: Field,
    [Uploader.name]: Uploader,
    [DatetimePicker.name]: DatetimePicker,
    [Popup.name]: Popup,
    [Picker.name]: Picker,
    [Dialog.Component.name]: Dialog.Component,
    [Switch.name]: Switch,
    [Icon.name]: Icon,
  },
  data() {
    return {
      // Logic Flags
      isNew: true,
      buttonLoading: false,
      titleTop: '',
      id: undefined,
      
      // Data Form
      dataForm: {
        diveLogId: undefined,
        dateOfStar: new Date(), // Standard JS Date
        dateOfLen: '', 
        weather: 'sky2',
        country: '',
        areaName: '',
        shopName: '',
        divePlaceName: '',
        maxDeep: '',
        avgDeep: '',
        visibility: '',
        temparature: '',
        about: '',
        res: '',
        styleId: '',
        isPrivate: false,
        diveType: 'SCUBA' 
      },

      fileList: [], 

      // UI States
      minDate: new Date(1900, 1, 1),
      maxDate: new Date(),
      tempDate: new Date(),
      tempTime: '12:00',
      
      showDatePicker: false,
      showTimePicker: false,
      showCountryPicker: false,
      showDiveTypePicker: false,
      showDepthPicker: false,
      showDurationPicker: false,
      showTempPicker: false,
      showVisPicker: false,
      showAvgDepthPicker: false,
      
      loadingCounty: false,
      
      // Data Lists
      countyList: [],
      diveTypeList: [],
      weatherList: ['sky2', 'sky1', 'sky4', 'sky3'],
      
      // Picker Columns
      columnsDepth: [],
      columnsDuration: [],
      columnsTemp: [],
      columnsVis: []
    }
  },
  created() {
    this.generatePickerData();
    this.diveTypeList = GetEnumList('Em_DiveType');
    this.maxDate = new Date();
    this.titleTop = GetDiveTitle();
    this.id = this.$route.params.id;
    this.isNew = !this.id || this.id === '0' || this.id === 0;
    
    // Fallback Countries
    this.fallbackCountries = [
        { text: 'China (中国)', value: 'CN' },
        { text: 'Philippines (菲律宾)', value: 'PH' },
        { text: 'Indonesia (印尼)', value: 'ID' },
        { text: 'Thailand (泰国)', value: 'TH' },
        { text: 'Malaysia (马来西亚)', value: 'MY' },
        { text: 'Maldives (马尔代夫)', value: 'MV' },
        { text: 'Japan (日本)', value: 'JP' },
        { text: 'Australia (澳洲)', value: 'AU' },
        { text: 'USA (美国)', value: 'US' },
        { text: 'Egypt (埃及)', value: 'EG' }
    ];

    if (!this.isNew) {
      this.init();
    } else {
      this.tempDate = new Date();
      // Ensure Defaults
      this.dataForm.diveType = 'SCUBA';
      this.dataForm.weather = 'sky2';
    }
  },
  methods: {
    GetEnumText,
    formatDate,
    
    // --- 1. Init Data ---
    generatePickerData() {
        for (let i = 1; i <= 50; i++) this.columnsDepth.push(i + '');
        for (let i = 0; i <= 40; i++) this.columnsTemp.push(i + '');
        this.columnsVis.push('0');
        for (let i = 5; i <= 50; i+=5) this.columnsVis.push(i + '');
        
        const hours = [];
        const mins = [];
        for(let i=0; i<=5; i++) hours.push(i + ' h');
        for(let i=0; i<=59; i++) mins.push(i + ' min');
        this.columnsDuration = [
            { values: hours, defaultIndex: 0 },
            { values: mins, defaultIndex: 45 } 
        ];
    },

    init() {
      request({
        url: '/Dive/getDiveLogEntity',
        method: 'post',
        params: { id: this.id }
      }).then(response => {
        if (!response.success) return;
        
        const d = response.data;
        // Fix: Use Object.assign to preserve reactivity of root object if needed
        // but replacing this.dataForm works in Vue 2 if structure is simple.
        // We carefully map fields to ensure types match.
        this.dataForm = {
            ...this.dataForm, // Keep defaults
            ...d,
            dateOfStar: new Date(d.dateOfStar),
            dateOfLen: d.dateOfLen ? d.dateOfLen + '' : '',
            maxDeep: d.maxDeep ? d.maxDeep + '' : '',
            avgDeep: d.avgDeep ? d.avgDeep + '' : '',
            visibility: d.visibility ? d.visibility + '' : '',
            temparature: d.temparature ? d.temparature + '' : '',
            // Ensure weather is valid
            weather: d.weather || 'sky2'
        };
        
        if (this.dataForm.res) {
          this.fileList = [];
          const resList = this.dataForm.res.split(',');
          resList.forEach(item => {
            if(item) this.fileList.push({ url: item, isNetwork: true });
          });
        }
      })
    },

    // --- 2. Picker Confirm Logic ---
    onConfrimDate(val) {
      const newDate = new Date(val);
      const oldDate = this.dataForm.dateOfStar;
      newDate.setHours(oldDate.getHours(), oldDate.getMinutes());
      this.dataForm.dateOfStar = newDate;
      this.showDatePicker = false;
    },
    onSelectTime() {
      // Fix: Ensure correct format HH:mm
      this.tempTime = formatDate(this.dataForm.dateOfStar, 'hh:mm');
      this.showTimePicker = true;
    },
    onConfrimTime(val) {
      if (!val) return;
      const [hours, mins] = val.split(':');
      const d = new Date(this.dataForm.dateOfStar);
      d.setHours(parseInt(hours), parseInt(mins));
      this.dataForm.dateOfStar = d;
      this.showTimePicker = false;
    },
    
    setWeather(item) {
        // Direct assignment works because dataForm.weather is already reactive
        this.dataForm.weather = item;
    },
    
    // Picker: Max Depth (Link to Avg)
    onConfirmMaxDepth(val) {
        this.dataForm.maxDeep = val;
        const depth = parseInt(val);
        if (!isNaN(depth)) {
            this.dataForm.avgDeep = Math.floor(depth / 2) + '';
        }
        this.showDepthPicker = false;
    },
    onConfirmAvgDepth(val) {
        this.dataForm.avgDeep = val;
        this.showAvgDepthPicker = false;
    },
    onConfirmTemp(val) {
        this.dataForm.temparature = val;
        this.showTempPicker = false;
    },
    onConfirmVis(val) {
        this.dataForm.visibility = val;
        this.showVisPicker = false;
    },
    onConfirmDuration(values) {
        const h = parseInt(values[0]);
        const m = parseInt(values[1]);
        const total = (h * 60) + m;
        this.dataForm.dateOfLen = total + '';
        this.showDurationPicker = false;
    },
    formatDurationDisplay(val) {
        if (!val) return '--';
        const total = parseInt(val);
        if (isNaN(total)) return val;
        const h = Math.floor(total / 60);
        const m = total % 60;
        if (h > 0) return `${h}h ${m}min`;
        return `${m}min`;
    },

    // --- 3. Country Logic ---
    onCountryPicker() {
        if (this.countyList.length === 0) {
            this.loadCountry();
        } else {
            this.showCountryPicker = true;
        }
    },
    loadCountry() {
      this.loadingCounty = true;
      request({
        url: '/DiveData/diveCountryList',
        method: 'post',
        params: { haveRecommend: true }
      }).then(response => {
        this.loadingCounty = false;
        if (response.success && response.data && response.data.length > 0) {
           this.countyList = response.data;
        } else {
           this.countyList = this.fallbackCountries;
        }
        this.showCountryPicker = true;
      }).catch(() => {
          this.loadingCounty = false;
          this.countyList = this.fallbackCountries;
          this.showCountryPicker = true;
      });
    },
    onConfrimCountry(select) {
        if (select.text && select.value) {
            this.dataForm.country = select.value;
        } else if (Array.isArray(select) && select.length > 0) {
             const item = select[select.length - 1];
             this.dataForm.country = item.code || item.value || item; 
        }
        this.showCountryPicker = false;
    },
    getCountryName(val) {
        if(!val) return '';
        const fb = this.fallbackCountries.find(c => c.value === val);
        if(fb) return fb.text;
        return val;
    },

    // --- 4. Misc Logic ---
    onConfrimDiveType(select) {
        this.dataForm.diveType = select.value || select;
        this.showDiveTypePicker = false;
    },
    triggerUpload() {
        if (this.$refs.uploader && this.$refs.uploader.chooseFile) {
            this.$refs.uploader.chooseFile();
        } else {
            const input = document.querySelector('.hidden-uploader input');
            if(input) input.click();
        }
    },
    afterRead(file) {
      console.log('File read:', file);
    },
    removeImg(index) {
        this.fileList.splice(index, 1);
    },
    
    uploadImg(callback) {
      const filesToUpload = [];
      const keptUrls = [];
      this.fileList.forEach(item => {
          if (item.file) filesToUpload.push(item.file);
          else if (item.url) keptUrls.push(item.url);
      });

      if (filesToUpload.length === 0) {
          callback(keptUrls.toString());
          return;
      }

      compressorImageList(filesToUpload, 1024, 1024, 0.6, (list) => {
        const formData = new FormData();
        list.forEach(value => {
            if (value.name) formData.append('file', value.file, value.name);
            else formData.append('file', value.file);
        });

        request({
          url: '/Data/uploadFile?config=divelog',
          method: 'post',
          data: formData
        }).then(response => {
          if (!response.success) {
            this.buttonLoading = false;
            Toast.fail('Image Upload Failed');
            return;
          }
          const allUrls = [...keptUrls];
          response.data.forEach(item => {
              allUrls.push(item.save);
          });
          callback(allUrls.toString());
        });
      });
    },

    onSave() {
      if (this.buttonLoading) return;
      if (!this.dataForm.divePlaceName) {
          Toast('请输入潜点名称 / Enter Dive Site');
          return;
      }
      if (!this.dataForm.dateOfLen) {
          Toast('请输入潜水时长 / Enter Time');
          return;
      }
      this.buttonLoading = true;
      Toast.loading({ message: 'Saving...', forbidClick: true });

      // Ensure Date format is standard for backend
      // Using utils or custom standard string
      // this.dataForm.dateOfStar is a Date Object here. 
      // We rely on request interceptor or formatting here.
      // Let's format manually to be safe: yyyy/MM/dd HH:mm:ss
      const d = this.dataForm.dateOfStar;
      const formattedDate = formatDate(d, 'yyyy/MM/dd hh:mm:ss');
      
      // Clone to avoid mutating UI state if request fails
      const payload = { ...this.dataForm, dateOfStar: formattedDate };

      this.uploadImg((imgStr) => {
        payload.res = imgStr;
        request({
          url: '/Dive/updateDiveLog',
          method: 'post',
          data: payload
        }).then(response => {
          this.buttonLoading = false;
          if (!response.success) {
             Toast.fail(response.msg || 'Save Failed');
             return;
          }
          Toast.success('Saved Successfully');
          this.goBack();
        });
      });
    },
    onDelete() {
      Dialog.confirm({
        title: 'DELETE LOG',
        message: 'Are you sure you want to delete this log?',
        confirmButtonColor: '#FF4757',
        className: 'dark-dialog'
      }).then(() => {
        if (this.buttonLoading) return;
        this.buttonLoading = true;
        request({
          url: '/Dive/deleteDiveLog',
          method: 'post',
          data: { ids: [this.id] }
        }).then(response => {
          this.buttonLoading = false;
          if (!response.success) return;
          Toast.success('Deleted');
          this.$router.push('/dive/logList');
        });
      });
    },
    goBack() {
      if (window.history.length > 1) {
           this.$router.go(-1);
      } else {
           this.$router.push('/dive/logList');
      }
    }
  }
}
</script>

<style scoped>
/* Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;700&family=Roboto:wght@300;400;500;700&display=swap');

.log-edit-page {
  --bg-deep: #050a14;
  --border-dim: rgba(255, 255, 255, 0.1); 
  --accent-cyan: #00E5FF;
  --accent-alert: #FF4757; 
  --text-white: #ffffff;
  --text-mute: rgba(255, 255, 255, 0.4);
  --input-bg: rgba(255,255,255,0.02);
  --popup-bg: #1a222e;

  background-color: var(--bg-deep);
  color: var(--text-white);
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  position: relative;
  padding-bottom: 80px;
}

.font-tech { font-family: 'Oswald', sans-serif; letter-spacing: 1px; text-transform: uppercase; }

/* --- Header --- */
.page-header {
    position: sticky; top: 0; z-index: 100;
    padding: 12px 20px;
    background: rgba(5, 10, 20, 0.96); border-bottom: 1px solid var(--border-dim);
    display: flex; justify-content: space-between; align-items: center; height: 48px; box-sizing: border-box;
}
.nav-title { font-size: 15px; font-weight: 600; letter-spacing: 1px; color: white; display:flex; align-items:center; }
.nav-subtitle { font-family:'Oswald'; font-size:12px; color:#00E5FF; margin-left:8px; margin-top:2px; }
.nav-icon { font-size: 20px; color: white; cursor: pointer; }
.nav-btn { font-size: 14px; color: var(--accent-cyan); font-weight: 500; cursor: pointer; padding: 5px; }
.nav-btn.disabled { opacity: 0.5; }

/* --- Form Wrapper --- */
.form-container { padding: 20px; }

.form-section-title {
    margin-top: 28px; margin-bottom: 16px; 
    display: flex; align-items: baseline; gap: 8px;
    border-bottom: 1px solid var(--border-dim); padding-bottom: 6px;
}
.fst-cn { font-size: 14px; color: white; font-weight: 600; }
.fst-en { font-size: 10px; color: var(--accent-cyan); font-family: 'Oswald'; letter-spacing: 1px; opacity: 0.8; }

/* Input Groups */
.hud-input-group { margin-bottom: 20px; }
.label-row { display: flex; align-items: baseline; gap: 6px; margin-bottom: 8px; }
.lbl-cn { font-size: 12px; color: rgba(255,255,255,0.7); }
.lbl-en { font-size: 9px; color: var(--accent-cyan); font-family: 'Oswald'; opacity: 0.6; letter-spacing: 0.5px; }

/* IMPORTANT: Wrapper for Input + Icon */
.input-wrapper { position: relative; display: flex; align-items: center; }
.input-arrow { position: absolute; right: 0; color: rgba(255,255,255,0.3); font-size: 16px; pointer-events: none; }

.hud-input {
    width: 100%; background: transparent; border: none;
    border-bottom: 1px solid var(--border-dim);
    color: white; font-size: 16px; padding: 8px 20px 8px 0; /* Right padding for arrow */
    border-radius: 0; transition: 0.3s;
    box-sizing: border-box;
    display: block; 
}
.hud-input.pointer { cursor: pointer; }
.hud-input:focus { border-bottom-color: var(--accent-cyan); outline:none; }
.hud-input::placeholder { color: rgba(255,255,255,0.15); font-size: 14px; }
.hud-input.is-num { font-family: 'Oswald'; font-size: 22px; letter-spacing: 1px; }

.input-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.input-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }

/* Weather Icons */
.weather-selector { display: flex; gap: 15px; }
.weather-icon-box {
    width: 40px; height: 40px; border-radius: 50%;
    background: rgba(255,255,255,0.05); border: 1px solid var(--border-dim);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.3s;
}
.weather-icon-box i { font-size: 24px; color: #888; }
.weather-icon-box.active { background: rgba(0,229,255,0.15); border-color: var(--accent-cyan); }
.weather-icon-box.active i { color: var(--accent-cyan); }

/* Switch Wrapper */
.switch-wrapper { padding-top: 5px; }

/* Visuals */
.hidden-uploader { display: none; }
.upload-zero-state {
    width: 100%; height: 120px;
    border: 1px dashed var(--border-dim); background: var(--input-bg);
    border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center;
    color: var(--text-mute); margin-bottom: 12px; transition: 0.3s; cursor: pointer;
}
.upload-zero-state:active { background: rgba(0, 229, 255, 0.05); border-color: var(--accent-cyan); }

.photo-list-scroll {
    display: flex; gap: 12px; overflow-x: auto; padding-bottom: 10px; margin-bottom: 10px;
    padding-right: 20px; scrollbar-width: none;
}
.photo-list-scroll::-webkit-scrollbar { display: none; }

.photo-item { position: relative; flex: 0 0 100px; height: 100px; }
.photo-thumb { width: 100%; height: 100%; object-fit: cover; border-radius: 4px; border: 1px solid var(--border-dim); }
.delete-badge {
    position: absolute; top: -6px; right: -6px;
    width: 20px; height: 20px; border-radius: 50%;
    background: var(--accent-alert); color: white;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: bold;
    box-shadow: 0 2px 5px rgba(0,0,0,0.5); z-index: 5;
}

.photo-add-small {
    flex: 0 0 80px; height: 100px;
    border: 1px dashed var(--accent-cyan); background: rgba(0, 229, 255, 0.05);
    border-radius: 4px; display: flex; flex-direction: column; align-items: center; justify-content: center;
    color: var(--accent-cyan); cursor: pointer;
}

.hud-textarea {
    width: 100%; background: var(--input-bg); border: 1px solid var(--border-dim);
    color: white; padding: 12px; font-size: 14px; line-height: 1.6;
    border-radius: 4px; resize: none; min-height: 100px; box-sizing: border-box;
}

/* Footer */
.fixed-footer {
    position: fixed; bottom: 0; left: 0; width: 100%;
    background: rgba(5, 10, 20, 0.95); border-top: 1px solid var(--border-dim);
    padding: 16px 20px; padding-bottom: calc(16px + env(safe-area-inset-bottom));
    z-index: 50; box-sizing: border-box;
}
.footer-btn-group { display: flex; gap: 10px; }
.submit-btn {
    flex: 1; height: 48px;
    border: none; border-radius: 2px;
    color: #050a14; font-size: 15px; font-weight: 600; letter-spacing: 1px;
    display: flex; align-items: center; justify-content: center; 
}
.submit-btn span { margin-left: 6px; font-family: 'Oswald'; font-weight: 500; font-size: 12px; }
.btn-save { background: var(--accent-cyan); }
.btn-delete { background: rgba(255,71,87,0.1); color: var(--accent-alert); border: 1px solid var(--accent-alert); flex: 0 0 80px; }
.submit-btn:disabled { opacity: 0.5; }

/* Vant Overrides for Dark Theme */
::v-deep .van-picker { background: var(--popup-bg); color: white; }
::v-deep .van-picker__mask { background-image: linear-gradient(180deg,rgba(26, 34, 46, 0.9),rgba(26, 34, 46, 0.4)),linear-gradient(0deg,rgba(26, 34, 46, 0.9),rgba(26, 34, 46, 0.4)) !important; }
::v-deep .van-picker-column__item { color: rgba(255,255,255,0.5); }
::v-deep .van-picker-column__item--selected { color: white; font-weight: bold; }
::v-deep .van-picker__confirm { color: var(--accent-cyan); }
::v-deep .van-picker__cancel { color: rgba(255,255,255,0.5); }
::v-deep .van-popup--bottom { background-color: var(--popup-bg); }

</style>